import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { isDriverHost } from '../lib/pwaHost';

/**
 * PwaManifestSwitcher
 *
 * Swaps the active web app manifest + apple-touch-icon + favicon + theme color
 * depending on whether the user is on the storefront or the driver portal.
 *
 * Why this is non-trivial: `index.html` ships with 10+ static `<link rel="icon">`
 * tags (svg + sizes 16…512). When the admin edits favicon_settings we MUST
 * remove every one of those before injecting a single dynamic /api/favicon
 * link, otherwise the browser picks one of the stale static PNGs and the
 * edit appears to have no effect.
 *
 * The component also re-runs whenever SettingsContext.settings.updated_at
 * changes (i.e. immediately after the admin saves), and includes a fresh
 * cache-buster query param so the browser refetches the SVG.
 */
const PwaManifestSwitcher = () => {
  const location = useLocation();
  // Per-path manifest swap — restores the dual home-screen-icon model:
  //   /driver/*  → driver manifest (yellow MR Driver icon, label "MR Driver",
  //                scope /driver, start_url /driver)
  //   everything else → storefront manifest (orange MR icon, label
  //                     "MR E-Store", scope /, start_url /)
  // iOS treats each Add-to-Home-Screen as an independent shortcut so
  // both icons coexist cleanly. Chrome/Android enforces one PWA per
  // overlapping scope on a single origin — Android users only get
  // whichever they install first; the second install attempt is
  // silently rejected. StandaloneRoleRouter handles the routing
  // when an Android driver launches the (single) installed app.
  const driverHost = isDriverHost();
  const isDriver = driverHost || location.pathname.startsWith('/driver');
  // Warehouse floor PWA — Feb 2026. Any path under `/warehouse/*` gets
  // the indigo Warehouse manifest so picker/loader phones (and shared
  // floor tablets) install a distinct home-screen icon that launches
  // straight into `/warehouse/floor`. Coexists with the storefront +
  // driver PWAs on iOS (separate Add-to-Home-Screen shortcuts); on
  // Chrome/Android the first install wins scope arbitration.
  //
  // SAFETY NET — login redirect: `/warehouse/*` normally uses the
  // dedicated `/warehouse/login` route so the URL never leaves the
  // warehouse scope during sign-in, but any stray link that still
  // routes through `/admin/login?next=/warehouse/...` should also keep
  // the warehouse manifest active. We inspect the `next` query param
  // on login-style paths and treat the whole session as warehouse
  // whenever `next` points back into `/warehouse`.
  const path = location.pathname || '';
  const search = location.search || '';
  const isLoginPath = (
    path === '/admin/login'
    || path === '/warehouse/login'
    || path === '/login'
  );
  let nextTarget = '';
  if (isLoginPath) {
    try {
      const raw = new URLSearchParams(search).get('next') || '';
      if (raw && raw.startsWith('/') && !raw.startsWith('//')) nextTarget = raw;
    } catch (_e) { /* noop */ }
  }
  const isWarehouse = path.startsWith('/warehouse') || nextTarget.startsWith('/warehouse');
  // Branch context — /branch/* always, plus /track/* when a branch
  // token is present (branch users click through from My Orders to the
  // public tracker and expect their portal favicon to persist).
  const isBranch = path.startsWith('/branch') || (path.startsWith('/track') && typeof window !== 'undefined' && !!window.localStorage?.getItem('branch_token'));
  const { settings } = useSettings();
  const branchFaviconUrl = settings?.branch_portal_favicon_url || '';
  // Any field updates `updated_at` on the settings doc; depend on it so the
  // favicon refetches the moment the admin saves their changes.
  const bust = settings?.updated_at || settings?._favicon_bust || 'init';

  useEffect(() => {
    const head = document.head;
    if (!head) return;

    // --- manifest ---
    // Use the DYNAMIC backend endpoint so the install prompt's icon set is
    // cache-busted on every admin save. The static /manifest.json + /driver-
    // manifest.json files are still useful as build-time placeholders, but
    // the live install prompt should always use the API endpoint.
    //
    // CRITICAL: the manifest URL MUST be SAME-ORIGIN as the document.
    // Chrome silently ignores `start_url` and `scope` (and refuses to show
    // the address-bar Install icon) if the manifest is loaded from a
    // different origin. Production runs on `estore.mrpco.net` but the
    // build may bake a different `REACT_APP_BACKEND_URL`, so we deliberately
    // drop the base URL here and let the K8s ingress route `/api/*` to the
    // same backend on the current origin.
    let manifestLink = head.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      head.appendChild(manifestLink);
    }
    // Per-path manifest swap (restores dual home-screen-icon model
    // for iOS — see component-level comment for the Android caveat).
    // Warehouse takes precedence over driver because a driver would
    // never load a `/warehouse/*` route; a warehouse picker never
    // loads `/driver/*` — the two are mutually exclusive paths.
    const manifestKind = isWarehouse ? 'warehouse' : (isDriver ? 'driver' : 'storefront');
    const scopeHint = driverHost ? '&host=driver' : '';
    const desired = `/api/manifest/${manifestKind}.json?v=${encodeURIComponent(bust)}${scopeHint}`;
    if (manifestLink.getAttribute('href') !== desired) {
      manifestLink.setAttribute('href', desired);
    }

    // --- apple-touch-icon (iOS home screen icon) — point at the BACKEND PNG
    // endpoint so admin edits propagate end-to-end. Path-dependent so the
    // driver portal installs the yellow MR-Driver icon while the
    // storefront installs the orange MR icon — two distinct home-screen
    // icons on iOS. Same-origin path for the same reason as the manifest.
    head.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => el.remove());
    const appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    appleIcon.href = `/api/favicon/${manifestKind}-180.png?v=${encodeURIComponent(bust)}`;
    head.appendChild(appleIcon);

    // --- apple-mobile-web-app-title (label under iOS home-screen icon) ---
    // Path-dependent — drivers see "MR Driver" under the yellow icon,
    // customers see "MR E-Store" under the orange icon.
    let appleTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
      head.appendChild(appleTitle);
    }
    appleTitle.setAttribute('content', isWarehouse ? 'MR Warehouse' : (isDriver ? 'MR Driver' : 'MR E-Store'));

    // --- theme color (browser chrome) ---
    let theme = head.querySelector('meta[name="theme-color"]');
    if (!theme) {
      theme = document.createElement('meta');
      theme.setAttribute('name', 'theme-color');
      head.appendChild(theme);
    }
    theme.setAttribute('content', isWarehouse ? '#4F46E5' : (isDriver ? '#FFD93D' : '#FFFFFF'));

    // --- tab favicon — render dynamically from /api/favicon/{kind}.svg ---
    // Same-origin path (see manifest comment above) — the tab favicon also
    // benefits from being same-origin so caches and CSP behave predictably.
    // Branch overrides everything with a plain uploaded image when the
    // admin has set one; storefront + driver still use the parametric
    // SVG generator.
    head.querySelectorAll('link[rel="icon"]').forEach((el) => el.remove());
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    if (isBranch && branchFaviconUrl) {
      // Uploaded raster/SVG — let the browser sniff the type from the URL.
      favicon.href = `${branchFaviconUrl}${branchFaviconUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(bust)}`;
    } else {
      favicon.type = 'image/svg+xml';
      favicon.href = `/api/favicon/${manifestKind}.svg?v=${encodeURIComponent(bust)}`;
    }
    head.appendChild(favicon);

    // --- application-name (matches the install label per app) ---
    let appName = head.querySelector('meta[name="application-name"]');
    if (!appName) {
      appName = document.createElement('meta');
      appName.setAttribute('name', 'application-name');
      head.appendChild(appName);
    }
    appName.setAttribute('content', isWarehouse ? 'MR Warehouse' : (isDriver ? 'MR Driver' : 'MR E-Store'));

    // --- document.title — the label iOS suggests in Add-to-Home-Screen
    // sheet. Path-dependent: drivers AHS-ing from /driver/* see
    // "MR Driver" as the suggested icon name; customers see the full
    // storefront title.
    const desiredTitle = isWarehouse
      ? 'MR Warehouse — Floor Portal'
      : (isDriver
        ? 'MR Driver — Dispatch Portal'
        : 'Mohammed Riaz & Partner LLC | E-Store');
    if (document.title !== desiredTitle) {
      document.title = desiredTitle;
    }
  }, [isDriver, driverHost, isWarehouse, isBranch, branchFaviconUrl, bust]);

  return null;
};

export default PwaManifestSwitcher;
